import { Api, ArrayUtil, Model, ModelRow, ModelSearchResult, UrlUtil } from "@mcleod/core";
import {
    ChangeEvent, DataSource, DataSourceMode, Label, Layout, LookupModelSearchEvent, LookupModelSelectionEvent, Panel,
    Textbox, Toast
} from "@mcleod/components";
import { CommonDialogs, YesNoDialogProps } from "@mcleod/common";
import { AutogenLayoutFileImportProfileList } from "./autogen/AutogenLayoutFileImportProfileList";
import { ImportFile } from "./ImportFile";

export class FileImportProfileList extends AutogenLayoutFileImportProfileList {
    override async onLoad() {
        this.tableFileImportProfile.addButtonCaption = "Add New";
        const props = UrlUtil.getPropsFromUrl(window.location.href);
        if (props?.dataType) {
            this.addLayoutLoadListener(() => {
                this.autoFillType(this.textboxDataTypeSearch, props.dataType);
            });
        }
    }

    private autoFillType(textbox: Textbox, id: string) {
        const modelRow = this.mainDataSource.searchRow;
        modelRow.set(textbox.field, id);
        const lookupLayout = Layout.getLayout(textbox.lookupModelLayout);
        lookupLayout.addLayoutLoadListener(() => {
            const filter = { search: id };
            lookupLayout.mainDataSource.search(filter, null, { layoutName: lookupLayout.layoutName }).then((response: ModelSearchResult) => {
                if (response.modelRows.length == 1) {
                    this.mainDataSource.searchRow.setValueAndLookupModelData("data_type", id, {
                        id: id,
                        description: response.modelRows[0].get("description", id)
                    });
                    this.textboxDataTypeSearch.displayComponentData(modelRow, null, 0);
                    this.mainDataSource.executeValidatedSearch();
                }
            });
        });
    }

    textboxDataTypeSearchOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            this.textboxTableNameSearch.clear();
            this.textboxFieldNameSearch.clear();
        }
    }

    private getSelectedDataTypes(): string[] {
        return this.textboxDataTypeSearch.lookupModelData?.map(lmDataRow => {
            return lmDataRow.get(this.textboxDataTypeSearch.lookupModelResultField);
        }) || [];
    }

    private addSelectedDataTypesToEvent(event: LookupModelSearchEvent) {
        const selectedDataTypes = this.getSelectedDataTypes();
        if (!ArrayUtil.isEmptyArray(selectedDataTypes)) {
            event.filter.data_types = selectedDataTypes;
        }
    }

    textboxTableNameSearchBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        this.addSelectedDataTypesToEvent(event);
    }

    textboxTableNameSearchOnChange(event: ChangeEvent) {
        const tableNameTextbox = event.target as Textbox;
        if (event.userInitiatedChange) {
            this.textboxFieldNameSearch.clear();
        }
        this.textboxFieldNameSearch.enabled = !tableNameTextbox.isEmpty();
    }

    textboxFieldNameSearchBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        this.addSelectedDataTypesToEvent(event);
        const selectedTableNames = this.textboxTableNameSearch.lookupModelData?.map(lmDataRow => {
            return lmDataRow.get(this.textboxTableNameSearch.lookupModelResultField);
        }) || [];
        event.filter.selected_table_name = selectedTableNames?.[0];
    }

    buttonImportOnClick() {
        const selectedRow = this.tableFileImportProfile.selectedRow;
        const selectedProfileId = selectedRow?.data?.get("id", null) ?? null;
        const selectedProfileDescr = selectedRow?.data?.get("descr", null) ?? null;
        ImportFile.openInSlideout(selectedProfileId, selectedProfileDescr);
    }

    async buttonCopyOnClick() {
        const selectedRow = this.tableFileImportProfile.selectedRow;
        if (selectedRow != null) {
            const selectedProfile = selectedRow.data as ModelRow;
            const [ newProfileId, newProfileDescr ] =
                await this.promptUserForNewProfileIdDescr(selectedProfile.get("id"));
            if (newProfileId == null) {
                return;
            }
            const body = {
                source_profile_id: selectedProfile.get("id", null),
                new_profile_id: newProfileId,
                new_profile_descr: newProfileDescr
            };
            Api.post("lme/general/dataimport/fileimport-profile-copy", body).then(async(response) => {
                const responseRow = ArrayUtil.getFirstElement(response?.data);
                if (responseRow) {
                    Toast.showSuccessToast(responseRow.message);
                    const newRow = await Model.searchSingleRecord("lme/general/dataimport/fileimport-profile-list",
                        { id: responseRow.new_profile_id }, this.layoutName);
                    this.tableFileImportProfile.addRow(newRow, null, { addToData: true, display: true });
                }
            });

        }
    }

    private async promptUserForNewProfileIdDescr(sourceProfileId: string):
        Promise<[ newProfileId: string, newProfileDescr: string ]> {
        const [ contentPanel, textboxCopyId, textboxCopyDescr ] = this.buildCopyPromptPanel(sourceProfileId);
        const dialogProps: Partial<YesNoDialogProps> = { noButtonCaption: "Cancel", yesButtonCaption: "Copy" };
        const proceeding = await CommonDialogs.showYesNo(contentPanel, "Create a Copy", dialogProps);
        return proceeding === true ? [ textboxCopyId.text, textboxCopyDescr.text ] : [ null, null ];
    }

    private buildCopyPromptPanel(sourceProfileId: string):
        [ contentPanel: Panel, textboxCopyId: Textbox, textboxCopyDescr: Textbox ] {
        const dataSource = new DataSource({ url: this.sourceFileImportProfileList.url });
        const messageText = `File import profile ${sourceProfileId} will be copied.  Please enter an ID and description for the new profile.`;
        const labelMessage = new Label({ text: messageText });
        const textboxCopyId = new Textbox({
            id: "textboxCopyId",
            dataSource,
            field: "id",
            required: true,
            placeholder: "Enter New ID",
            fillRow: true,
        });
        const textboxCopyDescr = new Textbox({
            id: "textboxCopyDescr",
            dataSource,
            field: "descr",
            required: true,
            placeholder: "Enter New Description",
            fillRow: true
        });
        dataSource.setRowsAndMode(DataSourceMode.ADD, [ new ModelRow(dataSource.url, true) ]);
        const contentPanel = Panel.createNoMarginNoPaddingPanel({
            components: [ labelMessage, textboxCopyId, textboxCopyDescr ]
        });
        return [ contentPanel, textboxCopyId, textboxCopyDescr ];
    }

    tableFileImportProfileOnSelect() {
        this.buttonCopy.enabled = this.tableFileImportProfile.selectedRowCount == 1;
    }
}
